import { getToken, removeToken } from './auth'
import { apiErrorMessage, newReference } from './apiErrorText'
import { announceMutation } from './apiEvents'
export { API_MUTATION_EVENT } from './apiEvents'

/** A failed request, typed (state system, 2026-09-04).
 *
 *  `.status` / `.statusText` / `.body` are what the server said; `.at` and
 *  `.reference` are what the officer can quote. The message is an officer
 *  sentence (lib/apiErrorText.ts), never "API Error 500" — every
 *  `e.message` render in the tree reads it. `status` 0 = no answer at all
 *  (the network, not the server). */
export class ApiError extends Error {
  readonly at: Date
  readonly reference: string
  constructor(
    public status: number,
    public statusText: string,
    public body?: unknown
  ) {
    const at = new Date()
    const reference = newReference()
    super(apiErrorMessage(status, at, reference))
    this.name = 'ApiError'
    this.at = at
    this.reference = reference
  }
}

/** The one place a failure is logged, under its reference, so support can
 *  find the full response behind the four characters the officer quotes. */
function logFailure(err: ApiError, method: string, url: string): ApiError {
  console.error(`[api] ${method} ${url} · ${err.message}`, err.body ?? '')
  return err
}

/** A fetch that never reached the server (offline, DNS, CORS, aborted)
 *  throws a TypeError with browser wording; it becomes a status-0 ApiError
 *  so the page renders the same designed state. */
async function reach(url: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(url, init)
  } catch (e) {
    if (e instanceof DOMException && e.name === 'AbortError') throw e
    throw logFailure(new ApiError(0, 'unreachable', e), init.method ?? 'GET', url)
  }
}

export interface ApiFetchOptions extends RequestInit {
  /** LOGIN TRACKING: a timer-driven fetch — the heartbeat polling the
   *  surfaces run every 12-20 s — is not the officer doing anything, so it
   *  must not move the 4-hour idle clock. The backend reads this header
   *  (nolos.auth.sessions) and skips the last-seen bump. Every call the
   *  officer causes (a navigation, a click) leaves it unset. */
  background?: boolean
}

/** The header the backend recognises as "not user-driven". */
export const BACKGROUND_HEADER = 'X-Nolos-Background'

/** Where a 401 sends the officer. The backend's session gate answers with
 *  a detail code the login page turns into a plain sentence: idled out
 *  (4 h of inactivity) or ended by an administrator; anything else is the
 *  ordinary expiry. */
async function expiredReason(response: Response): Promise<'idle' | 'revoked' | '1'> {
  try {
    const body = (await response.clone().json()) as { detail?: unknown }
    if (body?.detail === 'session_idle') return 'idle'
    if (body?.detail === 'session_revoked') return 'revoked'
  } catch {
    // no json body
  }
  return '1'
}

async function bounceOn401(response: Response): Promise<never> {
  const reason = await expiredReason(response)
  removeToken()
  const currentPath = window.location.pathname
  if (currentPath !== '/login') {
    window.location.href = `/login?expired=${reason}`
  }
  throw new ApiError(401, 'Unauthorized')
}

function buildHeaders(options: ApiFetchOptions): Headers {
  const token = getToken()
  const headers = new Headers(options.headers)
  if (token) {
    headers.set('Authorization', `Bearer ${token}`)
  }
  if (options.background) {
    headers.set(BACKGROUND_HEADER, '1')
  }
  return headers
}

export async function apiFetch<T = unknown>(
  url: string,
  options: ApiFetchOptions = {}
): Promise<T> {
  const { background: _background, ...init } = options
  const headers = buildHeaders(options)
  // FormData must NOT carry a manual Content-Type — the browser sets
  // `multipart/form-data` with the correct boundary itself. Only default to
  // JSON for non-FormData bodies.
  const isFormData = typeof FormData !== 'undefined' && init.body instanceof FormData
  if (!headers.has('Content-Type') && init.body && !isFormData) {
    headers.set('Content-Type', 'application/json')
  }

  const response = await reach(url, { ...init, headers })

  if (response.status === 401) {
    await bounceOn401(response)
  }

  if (!response.ok) {
    let body: unknown
    try {
      body = await response.json()
    } catch {
      // no json body
    }
    throw logFailure(
      new ApiError(response.status, response.statusText, body),
      init.method ?? 'GET',
      url
    )
  }

  announceMutation(url, init.method)
  if (response.status === 204) {
    return undefined as T
  }

  return response.json()
}

/**
 * Fetch a binary resource (e.g. a PDF) with the same auth handling as
 * apiFetch, returning a Blob. Used by the PDF viewer to stream document
 * bytes through the backend /content proxy — which works against both real
 * GCS and the fake-gcs emulator (no URL signing required).
 */
export async function apiFetchBlob(url: string, options: ApiFetchOptions = {}): Promise<Blob> {
  const { background: _background, ...init } = options
  const headers = buildHeaders(options)

  const response = await reach(url, { ...init, headers })

  if (response.status === 401) {
    await bounceOn401(response)
  }

  if (!response.ok) {
    throw logFailure(new ApiError(response.status, response.statusText), init.method ?? 'GET', url)
  }

  announceMutation(url, init.method)
  return response.blob()
}
